import React, { useEffect, useMemo, useState } from 'react';
import { SizeMe } from 'react-sizeme';
import { ApolloConsumer } from 'react-apollo';
import { withFlagsConsumer } from '../../../../FeatureFlags';
import CreditsInfo from '../components/CreditsInfo';
import SubscriptionCreditsInfo from '../components/SubscriptionsCreditsInfo';
import { CreditsConsumer } from '../../../index';
import { AuthorisationConsumer } from '../../../../AuthorisationWrapper';
import RequestProvider from '../../../../RequestProvider';
import { getFinanceBalance as getFinanceBalanceRequest } from '@core/shared/api/financeApi';
import { CURRENCY_BY_ENTITY_CODE } from '../../../../../common/constants/currency';
import useSignedImageUrl from '../../../../../common/hooks/useSignedImageUrl';

// Wraps SubscriptionCreditsInfo with a Finance balance fetch when
// finance-ledger-enabled is on. Fetch failures fall back silently —
// the dropdown still renders with credits-only.
function SubscriptionCreditsInfoWithFinance({
  getFinanceBalance,
  fallbackCurrency,
  ...props
}) {
  const [financeBalance, setFinanceBalance] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await getFinanceBalance({});
        if (cancelled) return;
        if (error) {
          console.error('Finance balance fetch returned error:', error);
          return;
        }
        if (data?.getFinanceBalance) {
          setFinanceBalance({
            creditsRemaining: data.getFinanceBalance.totalRemaining,
            dollarsRemaining: data.getFinanceBalance.dollarsRemaining,
            currency: data.getFinanceBalance.currency,
            poolRemaining: data.getFinanceBalance.poolRemaining,
            teamBalances: data.getFinanceBalance.teamBalances
          });
        }
      } catch (err) {
        console.error('Finance balance fetch failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getFinanceBalance]);

  return (
    <SubscriptionCreditsInfo
      {...props}
      financeLedgerEnabled
      creditsRemaining={financeBalance?.creditsRemaining}
      dollarsRemaining={financeBalance?.dollarsRemaining}
      currency={financeBalance?.currency || fallbackCurrency}
      poolRemaining={financeBalance?.poolRemaining}
      teamBalances={financeBalance?.teamBalances}
    />
  );
}

// Signs organisationInfo.imageUrl (raw S3 key) into a pre-signed URL before
// passing organisationInfo down to child components. Falls back to the raw
// value (or '') so placeholderText still triggers when there is no logo.
function WithSignedOrgLogo({ apolloClient, organisationId, organisationInfo, children }) {
  const signedImageUrl = useSignedImageUrl(
    apolloClient,
    organisationInfo?.imageUrl || '',
    organisationId
  );
  const signedOrganisationInfo = useMemo(
    () => organisationInfo ? { ...organisationInfo, imageUrl: signedImageUrl } : organisationInfo,
    [organisationInfo, signedImageUrl]
  );
  return children(signedOrganisationInfo);
}

function MaybeFinanceAwareSubscriptionCreditsInfo({
  financeLedgerEnabled,
  organisationId,
  organisationInfo,
  ...subProps
}) {
  const requests = useMemo(
    () => (organisationId ?
    { getFinanceBalance: getFinanceBalanceRequest(organisationId, { silent: true }) } :
    null),
    [organisationId]
  );
  const fallbackCurrency =
  CURRENCY_BY_ENTITY_CODE[organisationInfo?.entityCode] || undefined;

  return (
    <ApolloConsumer>
      {(apolloClient) =>
      <WithSignedOrgLogo
        apolloClient={apolloClient}
        organisationId={organisationId}
        organisationInfo={organisationInfo}>
        {(signedOrganisationInfo) => {
          if (!financeLedgerEnabled || !requests) {
            return <SubscriptionCreditsInfo organisationInfo={signedOrganisationInfo} {...subProps} />;
          }
          return (
            <RequestProvider requests={requests}>
              {({ getFinanceBalance }) =>
              <SubscriptionCreditsInfoWithFinance
                getFinanceBalance={getFinanceBalance}
                fallbackCurrency={fallbackCurrency}
                organisationInfo={signedOrganisationInfo}
                {...subProps} />}
            </RequestProvider>);
        }}
      </WithSignedOrgLogo>}
    </ApolloConsumer>);

}









function CreditsInfoContainer(props) {
  const { flags, openHubSpotConversationsWidget, onClickTopUpNow } = props;
  const { subscriptionsV22 = false, financeLedgerEnabled = false } = flags || {};
  return (
    <SizeMe>
      {({ size }) =>
      <AuthorisationConsumer>
          {({
          selectedOrganisation,
          isShootstaAdmin,
          isShootstaTechSupport
        }) =>
        <CreditsConsumer>
              {({
            state: {
              isUnlimited,
              creditType,
              userTeams,
              creditBalance,
              contractItemBalance,
              orgTeams
            }
          }) => subscriptionsV22 ?
          <MaybeFinanceAwareSubscriptionCreditsInfo
            financeLedgerEnabled={financeLedgerEnabled}
            organisationId={selectedOrganisation?.organisationId}
            isUnlimited={isUnlimited}
            creditType={creditType}
            teams={userTeams}
            creditBalance={creditBalance}
            contractItemBalance={contractItemBalance}
            organisationInfo={selectedOrganisation}
            hasAdminRole={
            selectedOrganisation.permissions.includes(
              'MANAGE_ORGANISATION'
            ) ||
            isShootstaAdmin ||
            isShootstaTechSupport}

            size={size}
            orgTeams={orgTeams}
            openHubSpotConversationsWidget={
            openHubSpotConversationsWidget}

            onClickTopUpNow={onClickTopUpNow} /> :


          <CreditsInfo
            isUnlimited={isUnlimited}
            creditType={creditType}
            teams={userTeams}
            creditBalance={creditBalance}
            onClickTopUpNow={onClickTopUpNow} />}



            </CreditsConsumer>}

        </AuthorisationConsumer>}

    </SizeMe>);

}

export default withFlagsConsumer(CreditsInfoContainer);